import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import CrudManager from "@/components/admin/CrudManager";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";

const TONE = { verified: "bg-emerald-50 text-emerald-800 border-emerald-200", needs_review: "bg-amber-50 text-amber-900 border-amber-200", incorrect: "bg-rose-50 text-rose-800 border-rose-200" };
const AiCheck = ({ r }) => (r.ai_check ? <div className="max-w-xs"><span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-semibold ${TONE[r.ai_check.status]}`} data-testid={`ai-check-${r.id}`}>{r.ai_check.status.replace("_", " ")}</span>{r.ai_check.notes && <p className="mt-1 text-[11px] text-[#635060] leading-snug">{r.ai_check.notes}</p>}</div> : <span className="text-xs text-[#8a7686]" data-testid={`ai-check-${r.id}`}>Not checked</span>);

const FIELDS = [
  { key: "institution_name", label: "Institution", required: true, wide: true },
  { key: "institution_type", label: "Type" },
  { key: "location", label: "Location" },
  { key: "programme", label: "Programme / course", required: true },
  { key: "class_range", label: "Applicable class range", placeholder: "9-12" },
  { key: "rank", label: "Display order within cluster (1 = first)", type: "number" },
  { key: "entrance_exam", label: "Entrance exam(s)" },
  { key: "admission_route", label: "Admission route" },
  { key: "eligibility", label: "Eligibility", type: "textarea" },
  { key: "official_website", label: "Official website (https://)" },
  { key: "data_source", label: "Data source" },
  { key: "last_verified_date", label: "Last verified (YYYY-MM-DD)" },
  { key: "academic_year", label: "Academic year", placeholder: "2025-26" },
  { key: "cluster_ids", label: "Relevant career clusters", type: "multi" },
  { key: "active", label: "Active", type: "bool" },
];

const COLUMNS = [
  { key: "institution_name", label: "Institution", render: (r) => <div><p className="font-semibold">{r.institution_name}</p><p className="text-xs text-[#635060]">{r.location}</p></div> },
  { key: "rank", label: "#" },
  { key: "programme", label: "Programme" },
  { key: "entrance_exam", label: "Entrance exam", render: (r) => <span className="font-mono text-xs">{r.entrance_exam || "-"}</span> },
  { key: "ai_check", label: "AI check (Sonnet 5.5)", render: (r) => <AiCheck r={r} /> },
];

export default function HigherEd() {
  const { data: clusters } = useFetch("/admin/career-clusters");
  const [busy, setBusy] = useState(false);
  const verify = async () => {
    setBusy(true);
    try { await api.post("/admin/higher-ed/ai-verify"); toast.success("AI verification started. Refresh in about 2 minutes to see results."); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#E9DFE7] bg-white p-4">
        <p className="text-sm text-[#635060] max-w-2xl">Every entry is double-checked by Claude Sonnet 5.5. Only <b>verified</b> entries are shown in reports; <b>needs review</b> and <b>incorrect</b> entries are hidden until fixed. Saving an entry re-checks it automatically.</p>
        <button onClick={verify} disabled={busy} className="btn-pill inline-flex items-center gap-2 bg-[#4F0341] text-white hover:bg-[#721862] disabled:opacity-60" data-testid="higher-ed-ai-verify-button"><ShieldCheck className="h-4 w-4" />Re-run AI verification</button>
      </div>
      <CrudManager testId="higher-ed" endpoint="/admin/higher-ed" eyebrow="Reference data" title="Colleges & universities (India)" itemLabel="institution"
        description="Indicative list of top colleges, courses and universities per career cluster, shown to Class 9-12 students (5 per top-3 cluster, in display order)."
        fields={FIELDS} columns={COLUMNS} options={clusters} />
    </div>
  );
}
